import React, { useCallback, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useAsyncSafeState } from 'ui-components/hooks/async-safe';
import PropTypes from 'prop-types';
import Global from '../../../../../store/global-selectors';
import { FormattedMessage } from 'react-intl';
import { createLookupHash } from '../../../../../services/hash-service';
import { entitiesLoad } from '../../../../../store/entities/entities.actions';
import { bulkUpdateAutoSplitOption, bulkLoadProducts } from '../../../../../store/products/products.api';
import { Modal } from 'ui-components//components/modal';
import { ModalHeader } from 'ui-components/components/modal/modal-header';
import { ModalBody } from 'ui-components/components/modal/modal-body';
import { ModalFooter } from 'ui-components/components/modal/modal-footer';
import { Button } from 'ui-components/components/button';
import { AutoSplitRadioButtons } from '../../../../../products/product-details/components/auto-split-radio-buttons';
import { useProductContext } from '../../product-context';
import { processBulkLoadResponse } from '../../../../../store/products/product-bulk-load-processor';
import { ModalService } from 'ui-components/services/modal-service';
import { ErrorModal } from '../../../../../components/modals/error-modal';
import { LoadingIndicator } from 'ui-components/components/loading-indicator';
import {
  DoNotAutoSplit,
  SplitOnCustomQty,
  MinimumSplitOnCustomQty,
} from '../../../../../products/product-details/constants/auto-split-options';
import messages from './messages';

import style from './autosplit-modal.module.less';

const AutoSplitModal = ({ onClose }) => {
  const dispatch = useDispatch();
  const { selectedProductIds } = useProductContext();
  const selectedProducts = useSelector((state) =>
    Global.selectEntitiesProductsByIds(state, {
      productIds: selectedProductIds,
    }),
  );
  const [autoSplitOption, setAutoSplitOption] = useState(DoNotAutoSplit);
  const [autoSplitCustomQuantity, setAutoSplitCustomQuantity] = useState(MinimumSplitOnCustomQty);
  const [isLoading, setIsLoading] = useAsyncSafeState(false);
  const [isSaveDisabled, setIsSaveDisabled] = useAsyncSafeState(false);

  const handleAutoSplitOptionChange = useCallback(
    (val) => {
      setIsSaveDisabled(val === SplitOnCustomQty && autoSplitCustomQuantity <= 0);
      setAutoSplitOption(val);
    },
    [setIsSaveDisabled, setAutoSplitOption, autoSplitCustomQuantity],
  );

  const updateAutoSplitCustomQty = useCallback(
    (val) => {
      setIsSaveDisabled(!val || val <= 0);
      setAutoSplitCustomQuantity(val);
    },
    [setIsSaveDisabled, setAutoSplitCustomQuantity],
  );

  const launchErrorModal = useCallback(() => {
    ModalService.launchModal({
      component: ErrorModal,
      props: {
        title: <FormattedMessage {...messages.failureTitle} />,
        message: <FormattedMessage {...messages.failureMessage} />,
      },
    });
  }, []);

  const onSave = useCallback(() => {
    setIsLoading(true);
    bulkUpdateAutoSplitOption(selectedProducts, autoSplitOption, autoSplitCustomQuantity)
      .promise.then(() => {
        bulkLoadProducts(selectedProductIds)
          .then((response) => {
            dispatch(
              entitiesLoad({
                entities: {
                  products: createLookupHash(processBulkLoadResponse(response) || [], 'productId'),
                },
              }),
            );
          })
          .then(onClose());
      })
      .catch(() => {
        setIsLoading(false);
        launchErrorModal();
      });
  }, [setIsLoading, selectedProducts, autoSplitOption, selectedProductIds, onClose, dispatch, launchErrorModal, autoSplitCustomQuantity]);

  return (
    <Modal>
      <ModalHeader>
        <FormattedMessage {...messages.modalHeader} />
      </ModalHeader>
      <ModalBody className={style['autosplit-modal-body']}>
        {isLoading ? (
          <LoadingIndicator className={style.loader} />
        ) : (
          <>
            <div className={style['bulk-apply-message']}>
              <FormattedMessage
                {...messages.bulkApplyMessage}
                values={{
                  b: (chunk) => <b>{chunk}</b>,
                  count: selectedProductIds.length,
                }}
              />
            </div>
            <AutoSplitRadioButtons
              className={style['autosplit-options']}
              currentQuantity={autoSplitCustomQuantity}
              disabled={false}
              onChange={handleAutoSplitOptionChange}
              selectedSplitOption={autoSplitOption}
              setAutoSplitCustomQty={updateAutoSplitCustomQty}
            />
          </>
        )}
      </ModalBody>
      {!isLoading && (
        <ModalFooter>
          <Button onClick={onClose} theme='tertiary'>
            <FormattedMessage {...messages.cancel} />
          </Button>
          <Button disabled={isSaveDisabled} onClick={onSave} theme='primary'>
            <FormattedMessage {...messages.save} />
          </Button>
        </ModalFooter>
      )}
    </Modal>
  );
};

AutoSplitModal.propTypes = {
  onClose: PropTypes.func.isRequired,
};

export default AutoSplitModal;
